<template>
  <QDialog v-model="showDialog">
    <QCard class="lease-inclusion-dialog full-width q-pa-md payment-breakdown-modal" style="max-width: 768px">
      <QCardSection class="column items-center q-pa-none q-ma-none full-width scroll">
        <QCardSection class="column q-pa-none full-width">
          <QBtn
            rounded
            class="calculator-dialog-close"
            flat
            :icon-right="closeIconCircle"
            size="16px"
            color="primary"
            align="left"
            @click="handleClose"
          >
            Close
          </QBtn>

          <QCardSection class="payment-breakdown-modal__content column full-width">
            <h2 id="payment-breakdown-modal-title" class="payment-breakdown-modal__title">Estimated payment breakdown</h2>

            <div class="payment-breakdown-modal__rows">
              <div class="payment-breakdown-modal__row">
                <div class="payment-breakdown-modal__row-main">
                  <div class="payment-breakdown-modal__vehicle-finance-icon" aria-hidden="true">
                    <VehicleFinanceIcon />
                  </div>
                  <span class="payment-breakdown-modal__row-label">Vehicle finance</span>
                </div>
                <span class="payment-breakdown-modal__row-value">{{ displayVehicleFinanceAmount }}</span>
              </div>

              <div class="payment-breakdown-modal__bundled">
                <div
                  id="payment-breakdown-bundled-trigger"
                  class="payment-breakdown-modal__row payment-breakdown-modal__row--toggle"
                  role="button"
                  tabindex="0"
                  :aria-expanded="bundledExpanded ? 'true' : 'false'"
                  aria-controls="payment-breakdown-bundled-panel"
                  @click="toggleBundled"
                  @keydown.enter.prevent="toggleBundled"
                  @keydown.space.prevent="toggleBundled"
                >
                  <div class="payment-breakdown-modal__row-main">
                    <div class="payment-breakdown-modal__bundled-costs-icon" aria-hidden="true">
                      <BundledRunningCostsIcon />
                    </div>
                    <span class="payment-breakdown-modal__row-label">Bundled running costs</span>
                  </div>
                  <div class="payment-breakdown-modal__row-right">
                    <span class="payment-breakdown-modal__row-value">{{ displayBundledRunningCostsAmount }}</span>
                    <QIcon :name="bundledExpanded ? chevronUpIcon : chevronDownIcon" size="22px" class="payment-breakdown-modal__chevron" />
                  </div>
                </div>

                <QSlideTransition>
                  <div
                    id="payment-breakdown-bundled-panel"
                    v-show="bundledExpanded"
                    role="region"
                    aria-labelledby="payment-breakdown-bundled-trigger"
                    class="payment-breakdown-modal__inclusions"
                  >
                    <p class="payment-breakdown-modal__inclusions-heading">What's included</p>
                    <ul class="payment-breakdown-modal__inclusion-list">
                      <li v-for="row in leaseInclusionIcons" :key="row.tag" class="payment-breakdown-modal__inclusion-item">
                        <div class="payment-breakdown-modal__inclusion-icon-wrap">
                          <MainIcon :icon="row.icon" />
                        </div>
                        <span class="payment-breakdown-modal__inclusion-label">{{ row.label }}</span>
                      </li>
                    </ul>
                  </div>
                </QSlideTransition>
              </div>
            </div>

            <div class="payment-breakdown-modal__summary">
              <p class="payment-breakdown-modal__summary-label">{{ paymentTotalLabel }}</p>
              <p class="payment-breakdown-modal__summary-amount">
                <span class="payment-breakdown-modal__summary-number">{{ displayEstimatedPayment }}</span>
                <span v-if="isNZ" class="payment-breakdown-modal__summary-suffix">ex gst</span>
                <span v-else class="payment-breakdown-modal__summary-suffix">incl gst</span>
              </p>
            </div>

            <p class="payment-breakdown-modal__disclaimer">
              This lease is for use of the vehicle only, with most running costs included in a {{ paymentCadenceNounPhrase }}. There is no
              ownership at any stage, and the vehicle is returned at the end of the lease.
            </p>
          </QCardSection>
        </QCardSection>
      </QCardSection>
    </QCard>
  </QDialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { QDialog, QCard, QCardSection, QBtn, QIcon, QSlideTransition } from 'quasar';
import { evaCloseCircleOutline, evaArrowIosDownward, evaArrowIosUpward } from '@quasar/extras/eva-icons';
import { LeaseInclusionIdentifierMixin } from '../../../mixins/calculator';
import { resolvePaymentBreakdownAmountDisplay } from '../../../utils/calculator/ratebookPaymentBreakdownFields';
import MainIcon from '../Icon/Inclusion/MainIcon.vue';
import VehicleFinanceIcon from '../Icon/PaymentBreakdown/VehicleFinanceIcon.vue';
import BundledRunningCostsIcon from '../Icon/PaymentBreakdown/BundledRunningCostsIcon.vue';

const CADENCE_VALUES = ['weekly', 'monthly'];

export default {
  name: 'PaymentBreakdown',
  mixins: [LeaseInclusionIdentifierMixin],
  components: {
    QDialog,
    QCard,
    QCardSection,
    QBtn,
    QIcon,
    QSlideTransition,
    MainIcon,
    VehicleFinanceIcon,
    BundledRunningCostsIcon
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    vehicleFinanceAmount: {
      type: String,
      default: ''
    },
    bundledRunningCostsAmount: {
      type: String,
      default: ''
    },
    estimatedPayment: {
      type: String,
      default: ''
    },
    paymentTotalLabel: {
      type: String,
      default: 'Estimated weekly payment'
    },
    /** Matches repayment cadence copy in the disclaimer */
    paymentCadence: {
      type: String,
      default: 'weekly',
      validator: (v) => CADENCE_VALUES.includes(v)
    }
  },
  data() {
    return {
      showDialog: false,
      bundledExpanded: false,
      closeIconCircle: evaCloseCircleOutline,
      chevronDownIcon: evaArrowIosDownward,
      chevronUpIcon: evaArrowIosUpward
    };
  },
  computed: {
    ...mapGetters({
      isNZ: 'theme/isNZ'
    }),
    displayVehicleFinanceAmount() {
      return resolvePaymentBreakdownAmountDisplay(this.vehicleFinanceAmount);
    },
    displayBundledRunningCostsAmount() {
      return resolvePaymentBreakdownAmountDisplay(this.bundledRunningCostsAmount);
    },
    displayEstimatedPayment() {
      return resolvePaymentBreakdownAmountDisplay(this.estimatedPayment);
    },
    paymentCadenceNounPhrase() {
      return this.paymentCadence === 'monthly' ? 'fixed monthly payment' : 'fixed weekly payment';
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.showDialog = val;
        if (!val) {
          this.bundledExpanded = false;
        }
      }
    },
    showDialog(val) {
      if (!val) {
        this.$emit('input', false);
        this.$emit('onClose', false);
      }
    }
  },
  methods: {
    handleClose() {
      this.showDialog = false;
    },
    toggleBundled() {
      this.bundledExpanded = !this.bundledExpanded;
    }
  }
};
</script>
